import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import KpiCards from "./dashboard/KpiCards";
import WeeklyPulse from "./dashboard/WeeklyPulse";
import DistributionCharts from "./dashboard/DistributionCharts";
import LoginsChart from "./dashboard/LoginsChart";
import StudentLists from "./dashboard/StudentLists";
import {
  countInRange,
  countFinished,
  distributionByBlock,
  distributionByField,
  loginsByDay,
  lastNDaysRange,
  daysSince,
} from "./dashboard/utils";

function LoadingSkeleton() {
  return (
    <div className="animate-pulse space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-24 bg-gray-200 rounded-xl" />
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-24 bg-gray-200 rounded-xl" />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-64 bg-gray-200 rounded-xl" />
        ))}
      </div>
      <div className="h-72 bg-gray-200 rounded-xl" />
    </div>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      const [students, templates, progress, logins, documents] =
        await Promise.all([
          supabase
            .from("students")
            .select("*")
            .order("created_at", { ascending: false }),
          supabase
            .from("activity_templates")
            .select("id, block_order, block_name")
            .eq("is_active", true),
          supabase
            .from("student_progress")
            .select("student_id, template_id, completed, completed_at, updated_at"),
          supabase
            .from("login_history")
            .select("student_id, logged_in_at"),
          supabase
            .from("documents")
            .select("id, created_at"),
        ]);

      setData({
        students: students.data || [],
        templates: templates.data || [],
        progress: progress.data || [],
        logins: logins.data || [],
        documents: documents.data || [],
      });
      setLoading(false);
    };

    fetchAll();
  }, []);

  if (loading || !data) return <LoadingSkeleton />;

  const { students, templates, progress, logins, documents } = data;

  // Bloque 1 - KPIs
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const startOfPrevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const endOfPrevMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);

  const newThisMonth = countInRange(students, "created_at", startOfMonth, now);
  const newPrevMonth = countInRange(
    students,
    "created_at",
    startOfPrevMonth,
    endOfPrevMonth
  );
  const avgProgress =
    students.length > 0
      ? Math.round(
          students.reduce((s, st) => s + (st.progress || 0), 0) / students.length
        )
      : 0;
  const finishedCount = countFinished(students, templates, progress);

  // Bloque 2 - Pulso semanal (últimos 7d vs 7d anteriores)
  const week1 = lastNDaysRange(7);
  const week2 = {
    from: new Date(week1.from.getTime() - 7 * 24 * 60 * 60 * 1000),
    to: new Date(week1.from.getTime() - 1),
  };

  const completedProgress = progress.filter((p) => p.completed && p.completed_at);

  // Bloque 3 - Distribuciones
  const byBlock = distributionByBlock(students, templates, progress);
  const byProgram = distributionByField(students, "program");
  const byStage = distributionByField(students, "stage");

  // Bloque 4 - Logins por día
  const last30 = lastNDaysRange(30);
  const logins30d = logins.filter(
    (l) => new Date(l.logged_in_at).getTime() >= last30.from.getTime()
  );
  const loginsChart = loginsByDay(logins30d, 30);

  // Bloque 5 - Listados accionables
  const lastLoginMap = new Map();
  for (const l of logins) {
    const t = new Date(l.logged_in_at).getTime();
    const prev = lastLoginMap.get(l.student_id);
    if (!prev || t > prev) lastLoginMap.set(l.student_id, t);
  }

  const studentsWithLogin = students.map((s) => ({
    ...s,
    lastLoginAt: lastLoginMap.get(s.id) || null,
    daysSinceLogin: daysSince(lastLoginMap.get(s.id)),
  }));

  const recent = studentsWithLogin.slice(0, 5);
  const lowProgress = studentsWithLogin
    .filter((s) => (s.progress || 0) < 30)
    .sort((a, b) => (a.progress || 0) - (b.progress || 0))
    .slice(0, 5);
  const inactive = studentsWithLogin
    .filter((s) => s.daysSinceLogin === null || s.daysSinceLogin > 14)
    .sort((a, b) => {
      const da = a.daysSinceLogin ?? Infinity;
      const db = b.daysSinceLogin ?? Infinity;
      return db - da;
    })
    .slice(0, 5);
  const almostDone = studentsWithLogin
    .filter((s) => (s.progress || 0) >= 80 && (s.progress || 0) < 100)
    .sort((a, b) => (b.progress || 0) - (a.progress || 0))
    .slice(0, 5);

  return (
    <div className="space-y-6">
      <KpiCards
        totalStudents={students.length}
        newThisMonth={newThisMonth}
        newPrevMonth={newPrevMonth}
        avgProgress={avgProgress}
        finishedCount={finishedCount}
      />

      <WeeklyPulse
        loginsThisWeek={countInRange(logins, "logged_in_at", week1.from, week1.to)}
        loginsPrevWeek={countInRange(logins, "logged_in_at", week2.from, week2.to)}
        activitiesThisWeek={countInRange(
          completedProgress,
          "completed_at",
          week1.from,
          week1.to
        )}
        activitiesPrevWeek={countInRange(
          completedProgress,
          "completed_at",
          week2.from,
          week2.to
        )}
        documentsThisWeek={countInRange(documents, "created_at", week1.from, week1.to)}
        documentsPrevWeek={countInRange(documents, "created_at", week2.from, week2.to)}
      />

      <DistributionCharts
        byBlock={byBlock}
        byProgram={byProgram}
        byStage={byStage}
      />

      <LoginsChart data={loginsChart} />

      <StudentLists
        recent={recent}
        lowProgress={lowProgress}
        inactive={inactive}
        almostDone={almostDone}
      />
    </div>
  );
}
